
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useBoards } from "@/contexts/boards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ColorPicker from "@/components/common/ColorPicker";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tier1Tracking } from "@/services/analytics/Tier1Tracking";

interface AddBoardFormProps {
  onSuccess?: () => void;
  onComplete?: () => void;
}

const AddBoardForm = ({ onSuccess, onComplete }: AddBoardFormProps) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#8B5CF6");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const { refreshBoards } = useBoards();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Board name is required");
      return;
    }

    if (!user) {
      toast.error("You must be logged in to create a board");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from("boards")
        .insert({
          title: name.trim(), // Use 'title' instead of 'name' for boards table
          color,
          user_id: user.id,
          display_order: 0, // Add required display_order field
        })
        .select();

      if (error) {
        console.error("Error creating board:", error);
        toast.error("Failed to create board");
        return;
      }

      if (data && data.length > 0) {
        const newBoard = data[0];
        
        // Track board creation with Tier 1 analytics
        Tier1Tracking.trackBoardCreated(
          newBoard.id,
          newBoard.title, // Use 'title' instead of 'name'
          newBoard.color,
          user
        );

        toast.success("Board created successfully");
        setName("");
        setColor("#8B5CF6");
        refreshBoards();
        
        if (onSuccess) {
          onSuccess();
        }
        if (onComplete) {
          onComplete();
        }
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="board-name">Board Name</Label>
        <Input
          id="board-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter board name"
          required
        />
      </div>
      
      <div>
        <Label htmlFor="board-color">Color</Label>
        <ColorPicker 
          value={color} 
          onChange={setColor}
        />
      </div>
      
      <Button 
        type="submit" 
        disabled={isSubmitting || !name.trim()}
        className="w-full"
      >
        {isSubmitting ? "Creating..." : "Create Board"}
      </Button>
    </form>
  );
};

export default AddBoardForm;
